import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { batchStore } from "@/stores/batchStore";
import { Trash2, RefreshCw, Loader2, ArrowUpRight, History as HistoryIcon, Package, ImageOff, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Batches — history of processed batches.
 *
 * Each row shows summary counts (total, ready, no-images, published) and the
 * "Reabrir" button loads it into the batchStore + navigates to /publish so the
 * user can pick up exactly where they left off, WITHOUT re-running LLM.
 */
export default function Batches() {
  const [batches, setBatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(null); // batch id being opened
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/batches");
      setBatches(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar lotes");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reopen = async (b) => {
    setOpening(b.id);
    try {
      const { data } = await api.get(`/batches/${b.id}`);
      // Load into module-level batchStore so /publish renders it immediately
      batchStore.loadFromBatch({
        rows: data.rows || [],
        fileName: data.file_name || "",
        headersDetected: !!data.headers_detected,
        prepProgress: data.prep_progress || { done: 0, total: 0 },
        pubProgress: data.pub_progress || { done: 0, total: 0 },
        llmCost: Number(data.llm_cost) || 0,
        batchId: b.id,
        batchName: b.name || "",
      });
      toast.success(`"${b.name}" reaberto — ${data.rows?.length || 0} itens`);
      navigate("/publish");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao reabrir lote");
    } finally {
      setOpening(null);
    }
  };

  const del = async (b) => {
    if (!window.confirm(`Apagar o lote "${b.name}"? Os dados desse lote no Magic SIP serão removidos (não afeta produtos já publicados nos ERPs).`)) return;
    setDeleting(b.id);
    try {
      await api.delete(`/batches/${b.id}`);
      setBatches((s) => s.filter((x) => x.id !== b.id));
      toast.success("Lote removido");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao apagar");
    } finally {
      setDeleting(null);
    }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen flex bg-zinc-50">
      <Sidebar />
      <Toaster position="top-right" />
      <main className="flex-1 min-w-0 p-8">
        <div className="flex items-center justify-between mb-1">
          <span className="eyebrow text-zinc-500">HISTÓRICO</span>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            className="rounded-md border-zinc-300 h-8 text-xs"
            data-testid="batches-refresh"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Atualizar
          </Button>
        </div>
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
          <HistoryIcon className="w-7 h-7 text-[#002FA7]" />
          Lotes salvos
        </h1>
        <p className="text-sm text-zinc-600 mb-6 max-w-2xl">
          Reabra qualquer lote anterior no exato ponto onde você parou — sem gastar tokens de IA de novo. O lote atual em <code className="bg-zinc-200 rounded px-1 text-xs">/publish</code> é salvo automaticamente.
        </p>

        {loading && !batches ? (
          <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</div>
        ) : (batches || []).length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-lg p-10 text-center bg-white" data-testid="batches-empty">
            <Package className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
            <p className="text-sm text-zinc-600">Você ainda não salvou nenhum lote.</p>
            <p className="text-xs text-zinc-400 mt-1">Assim que preparar seu primeiro lote em <strong>/publish</strong>, ele aparece aqui.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="batches-list">
            {batches.map((b) => (
              <div
                key={b.id}
                className="bg-white border border-zinc-200 rounded-lg p-4 flex items-center gap-4 hover:border-[#002FA7]/40 transition-colors"
                data-testid={`batch-row-${b.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-zinc-900 truncate">{b.name}</span>
                    {b.file_name && (
                      <span className="text-xs text-zinc-400 font-mono truncate">· {b.file_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Atualizado em {fmtDate(b.updated_at)}
                  </div>
                  <div className="flex gap-3 mt-2 text-xs font-mono">
                    <span className="text-zinc-700"><Package className="w-3 h-3 inline mr-1" />{b.total}</span>
                    {b.ready > 0 && <span className="text-emerald-700"><CheckCircle2 className="w-3 h-3 inline mr-1" />{b.ready} prontos</span>}
                    {b.no_img > 0 && <span className="text-amber-700"><ImageOff className="w-3 h-3 inline mr-1" />{b.no_img} s/ imagem</span>}
                    {b.published > 0 && <span className="text-[#002FA7]"><ArrowUpRight className="w-3 h-3 inline mr-1" />{b.published} publicados</span>}
                    {b.llm_cost > 0 && <span className="text-violet-700">≈ ${Number(b.llm_cost).toFixed(3)} IA</span>}
                  </div>
                </div>
                <Button
                  onClick={() => reopen(b)}
                  disabled={opening === b.id}
                  className="rounded-md brand-primary text-white h-9"
                  data-testid={`batch-reopen-${b.id}`}
                >
                  {opening === b.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                  Reabrir
                </Button>
                <button
                  onClick={() => del(b)}
                  disabled={deleting === b.id}
                  className="text-zinc-400 hover:text-red-600 p-2 rounded-md hover:bg-red-50 transition-colors"
                  data-testid={`batch-delete-${b.id}`}
                  title="Apagar este lote (não afeta produtos já publicados)"
                >
                  {deleting === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
